import { isPlainObject, looksLikeMarkdown, LLM_NODE_TYPE_RE } from "./detect";

export interface ResultEntry {
  key: string;
  value: unknown;
}

export interface HeadlineEntry {
  /** Undefined when the whole result is itself the headline (bare string/array). */
  key: string | undefined;
  value: unknown;
}

export interface ResultSplit {
  headline: HeadlineEntry | null;
  body: ResultEntry[];
  metadata: ResultEntry[];
  /** True when the result is null/empty — nothing to render. */
  isEmpty: boolean;
  /** True when the result has only metadata keys. */
  metadataOnly: boolean;
}

const CONTENT_KEYS = ["output", "text", "message", "body", "content", "result", "data"];

const KNOWN_META_KEYS = new Set([
  "model",
  "usage",
  "cost",
  "cost_details",
  "is_byok",
  "prompt_tokens",
  "completion_tokens",
  "completion_tokens_details",
  "prompt_tokens_details",
  "executiontime",
  "blocknumber",
]);

function isMetadataKey(key: string): boolean {
  if (key.startsWith("_")) return true;
  return KNOWN_META_KEYS.has(key.toLowerCase());
}

function isRichValue(value: unknown): boolean {
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (isPlainObject(value)) return Object.keys(value).length > 0;
  return false;
}

/** Pick the headline key from a result object, or null if none qualifies. */
function pickHeadlineKey(obj: Record<string, unknown>, nodeType: string): string | null {
  const isLlm = LLM_NODE_TYPE_RE.test(nodeType);
  if (isLlm) {
    for (const k of ["output", "text", "message"]) {
      if (k in obj && isRichValue(obj[k])) return k;
    }
  }
  for (const k of CONTENT_KEYS) {
    if (k in obj && !isMetadataKey(k) && isRichValue(obj[k])) return k;
  }
  // Fallback: the longest markdown-ish string among non-metadata keys.
  let best: { key: string; len: number } | null = null;
  for (const [k, v] of Object.entries(obj)) {
    if (isMetadataKey(k)) continue;
    if (typeof v === "string" && looksLikeMarkdown(v)) {
      if (!best || v.length > best.len) best = { key: k, len: v.length };
    }
  }
  return best?.key ?? null;
}

/** Split a node result into headline / body / metadata buckets. Top-level only. */
export function splitResult(result: unknown, nodeType: string): ResultSplit {
  const empty: ResultSplit = {
    headline: null,
    body: [],
    metadata: [],
    isEmpty: true,
    metadataOnly: false,
  };

  if (result == null) return empty;

  // Bare string or array — the whole value is the headline.
  if (typeof result === "string") {
    if (result.trim().length === 0) return empty;
    return { headline: { key: undefined, value: result }, body: [], metadata: [], isEmpty: false, metadataOnly: false };
  }
  if (Array.isArray(result)) {
    if (result.length === 0) return empty;
    return { headline: { key: undefined, value: result }, body: [], metadata: [], isEmpty: false, metadataOnly: false };
  }
  if (!isPlainObject(result)) {
    return { headline: { key: undefined, value: result }, body: [], metadata: [], isEmpty: false, metadataOnly: false };
  }

  const entries = Object.entries(result);
  if (entries.length === 0) return empty;

  const headlineKey = pickHeadlineKey(result, nodeType);
  const headline: HeadlineEntry | null = headlineKey != null ? { key: headlineKey, value: result[headlineKey] } : null;

  const body: ResultEntry[] = [];
  const metadata: ResultEntry[] = [];
  for (const [key, value] of entries) {
    if (key === headlineKey) continue;
    if (isMetadataKey(key)) {
      metadata.push({ key, value });
    } else {
      body.push({ key, value });
    }
  }

  return {
    headline,
    body,
    metadata,
    isEmpty: false,
    metadataOnly: headline == null && body.length === 0 && metadata.length > 0,
  };
}
